/**
 * Hub Registry - Helper functions for navigation, SSO, breadcrumbs, etc.
 */

import type { HubId, HubCategory, ExternalLink } from '../../components/types'
import type { HubDefinition } from './types'
import { HUB_REGISTRY } from './index'

/** Developer Hub lives on dashboard — there is no developer.quotech.ai host. */
const DEVELOPER_HUB_BASE = 'https://dashboard.quotech.ai/developer'

function resolveHubBaseUrl(hubId: HubId, baseUrl: string): string {
  if (hubId === 'developer' || baseUrl.includes('developer.quotech.ai')) {
    return DEVELOPER_HUB_BASE
  }
  return baseUrl
}

/**
 * Get external links for a hub (excludes the current hub).
 * Links use the defaultLandingPage for each hub.
 */
export function getExternalLinks(
  currentHubId: HubId
): (ExternalLink & { status?: 'production' | 'development' | 'planned' })[] {
  return Object.values(HUB_REGISTRY)
    .filter((hub) => hub.id !== currentHubId)
    .map((hub) => ({
      id: hub.id,
      label: hub.name,
      href: `${resolveHubBaseUrl(hub.id, hub.baseUrl)}${hub.defaultLandingPage}`,
      icon: hub.icon,
      category: hub.category,
      status: hub.status,
      description: hub.description,
      pages: hub.pages
        .filter((p) => !p.href.includes(':'))
        .slice(0, 8)
        .map((p) => ({
          label: p.label,
          href: `${hub.baseUrl}${p.href}`,
          icon: p.icon,
          description: p.description,
        })),
    }))
}

/** Get external links organized by category */
export function getExternalLinksByCategory(
  currentHubId: HubId
): Record<HubCategory, ReturnType<typeof getExternalLinks>> {
  const links = getExternalLinks(currentHubId)
  const result: Record<HubCategory, typeof links> = {
    acquire: [],
    deliver: [],
    manage: [],
    intelligence: [],
  }
  links.forEach((link) => {
    if (link.category) result[link.category].push(link)
  })
  return result
}

/** Get hub by ID */
export function getHub(hubId: HubId): HubDefinition | undefined {
  return HUB_REGISTRY[hubId]
}

/** Get the default landing page for a hub */
export function getHubLandingPage(hubId: HubId): string {
  const hub = HUB_REGISTRY[hubId]
  return hub ? hub.defaultLandingPage : '/'
}

/** Get the primary page (main workflow) for a hub */
export function getHubPrimaryPage(hubId: HubId): HubDefinition['pages'][0] | undefined {
  const hub = HUB_REGISTRY[hubId]
  if (!hub) return undefined
  return hub.pages.find((p) => p.isPrimary) || hub.pages[0]
}

/** Get all hubs sorted by category */
export function getHubsByCategory(): Record<HubCategory, HubDefinition[]> {
  const result: Record<HubCategory, HubDefinition[]> = {
    acquire: [],
    deliver: [],
    manage: [],
    intelligence: [],
  }
  Object.values(HUB_REGISTRY).forEach((hub) => result[hub.category].push(hub))
  return result
}

/** Get production-ready hubs only */
export function getProductionHubs(): HubDefinition[] {
  return Object.values(HUB_REGISTRY).filter((hub) => hub.status === 'production')
}

/** Get all hubs as a flat array */
export function getAllHubs(): HubDefinition[] {
  return Object.values(HUB_REGISTRY)
}

/**
 * Build SSO link to another hub.
 * Uses defaultLandingPage by default unless returnTo is specified.
 */
export function buildHubSSOLink(
  targetHub: HubId,
  token: string,
  orgId?: string,
  orgName?: string,
  returnTo?: string
): string {
  const hub = HUB_REGISTRY[targetHub]
  if (!hub) throw new Error(`Unknown hub: ${targetHub}`)

  const params = new URLSearchParams()
  params.set('token', token)
  if (orgId) params.set('org_id', orgId)
  if (orgName) params.set('org_name', orgName)

  const targetPath = returnTo || hub.defaultLandingPage
  if (targetPath && targetPath !== '/') params.set('returnTo', targetPath)

  const base = resolveHubBaseUrl(targetHub, hub.baseUrl)
  return `${base}/sso?${params.toString()}`
}

/** Build a direct link to a hub (without SSO) */
export function buildHubDirectLink(targetHub: HubId, path?: string): string {
  const hub = HUB_REGISTRY[targetHub]
  if (!hub) throw new Error(`Unknown hub: ${targetHub}`)
  const base = resolveHubBaseUrl(targetHub, hub.baseUrl)
  return `${base}${path || hub.defaultLandingPage}`
}

/** Check if a path matches a hub's pages */
export function isValidHubPath(hubId: HubId, path: string): boolean {
  const hub = HUB_REGISTRY[hubId]
  if (!hub) return false

  const normalizedPath = path.startsWith('/') ? path : `/${path}`
  return hub.pages.some((page) => {
    if (page.href.includes(':')) {
      const pattern = page.href.replace(/:[^/]+/g, '[^/]+')
      return new RegExp(`^${pattern}$`).test(normalizedPath)
    }
    return page.href === normalizedPath
  })
}

/**
 * Get breadcrumb data for a path within a hub.
 * Universal format: Q (Home) > Hub > Page
 */
export function getHubBreadcrumbs(
  hubId: HubId,
  path: string
): Array<{ label: string; href?: string }> {
  const hub = HUB_REGISTRY[hubId]
  if (!hub) return []

  const jobsHub = HUB_REGISTRY['jobs' as HubId]
  const jobsHref = jobsHub ? `${jobsHub.baseUrl}/jobs-dashboard` : 'https://dashboard.quotech.ai/jobs-dashboard'

  const breadcrumbs: Array<{ label: string; href?: string }> = [
    { label: 'Q', href: jobsHref },
    { label: hub.shortName, href: hub.defaultLandingPage },
  ]

  const normalizedPath = path.startsWith('/') ? path : `/${path}`
  const page = hub.pages.find((p) => {
    if (p.href.includes(':')) {
      const pattern = p.href.replace(/:[^/]+/g, '[^/]+')
      return new RegExp(`^${pattern}$`).test(normalizedPath)
    }
    return p.href === normalizedPath
  })

  if (page && page.href !== hub.defaultLandingPage) {
    breadcrumbs.push({ label: page.label })
  }

  return breadcrumbs
}
